import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

const AdminMySupportTicketDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (ticketId) {
      loadTicketDetails();
    }
  }, [ticketId]);

  const loadTicketDetails = async () => {
    try {
      setLoading(true);
      console.log('🎫 Loading my assigned ticket details for ID:', ticketId);
      const response = await adminApiService.getMyAssignedTicketById(ticketId);
      console.log('📊 My Assigned Ticket Details API Response:', response);
      
      if (response.success) {
        setTicket(response.data);
        console.log('✅ My assigned ticket details loaded successfully');
      } else {
        console.error('❌ My Assigned Ticket Details API returned error:', response.message);
        toast.error(response.message || 'Failed to load ticket details');
        navigate('/admin/dashboard/admin-assigned-tickets');
      }
    } catch (error) {
      console.error('❌ My assigned ticket details load error:', error);
      toast.error('Failed to load ticket details');
      navigate('/admin/dashboard/admin-assigned-tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setSubmittingComment(true);
      console.log('💬 Adding comment to my assigned ticket:', ticketId);
      
      const response = await adminApiService.addCommentToMyAssignedTicket(ticketId, comment.trim());
      
      if (response.success) {
        toast.success('Comment added successfully');
        setComment('');
        // Reload ticket details to show the new comment
        await loadTicketDetails();
      } else {
        toast.error(response.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('❌ Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'ASSIGNED': 'bg-blue-100 text-blue-800',
      'RESOLVE': 'bg-green-100 text-green-800',
      'INPROGRESS': 'bg-yellow-100 text-yellow-800',
      'OPEN': 'bg-gray-100 text-gray-800',
      'CLOSED': 'bg-red-100 text-red-800'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {status || 'UNKNOWN'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ticket Not Found</h2>
          <p className="text-gray-600 mb-4">The requested ticket could not be found.</p>
          <button
            onClick={() => navigate('/admin/dashboard/admin-assigned-tickets')}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
          >
            Back to My Support Tickets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Support Ticket Details</h1>
              <p className="text-gray-600">View and manage assigned ticket information</p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard/admin-assigned-tickets')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Back to My Tickets
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Ticket Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Ticket Information</h3>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Agent Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                    <input
                      type="text"
                      value={ticket.agentName || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent Email</label>
                    <input
                      type="text"
                      value={ticket.agentEmail || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  {/* Ticket Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ticket ID</label>
                    <input
                      type="text"
                      value={ticket.ticketId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID</label>
                    <input
                      type="text"
                      value={ticket.agentId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  {/* Related IDs */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Booking ID</label>
                    <input
                      type="text"
                      value={ticket.bookingId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment ID</label>
                    <input
                      type="text"
                      value={ticket.paymentId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund ID</label>
                    <input
                      type="text"
                      value={ticket.refundId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation ID</label>
                    <input
                      type="text"
                      value={ticket.cancellationId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  {/* Dates and Assignment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                    <input
                      type="text"
                      value={formatDate(ticket.createdDate)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                    <input
                      type="text"
                      value={ticket.assignedTo || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To Name</label>
                    <input
                      type="text"
                      value={ticket.assignedToName || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resolve Date</label>
                    <input
                      type="text"
                      value={formatDate(ticket.resolveDate)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resolver Name</label>
                    <input
                      type="text"
                      value={ticket.ticketResolveByName || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>
                </div>

                {/* Agent Query */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agent Query</label>
                  <textarea
                    value={ticket.agentQuery || '-'}
                    readOnly
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Status</h3>
              </div>
              <div className="p-6">
                <div className="flex justify-center">
                  {getStatusBadge(ticket.ticketStatus)}
                </div>
              </div>
            </div>

            {/* Add Comment Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Add Comment</h3>
              </div>
              <div className="p-6">
                <form onSubmit={handleAddComment}>
                  <div className="mb-4">
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Enter your comment here..."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none"
                      disabled={submittingComment}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submittingComment || !comment.trim()}
                    className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingComment ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Adding Comment...
                      </div>
                    ) : (
                      'Add Comment'
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Comments Section */}
            {ticket.comments && ticket.comments.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Comments ({ticket.comments.length})</h3>
                </div>
                <div className="p-6 max-h-96 overflow-y-auto">
                  <div className="space-y-4">
                    {ticket.comments.map((comment, index) => (
                      <div key={index} className="border-l-4 border-red-500 pl-4 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {comment.supportName || 'Admin'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(comment.cratedDate)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{comment.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMySupportTicketDetails;